import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import { forwardRef, type ButtonHTMLAttributes } from 'react';
import { cn } from '@/lib/utils';

/**
 * Button.
 *
 * Five variants, four sizes. Customized vs. shadcn's stock so the
 * defaults match the captured dash.cloudflare.com look:
 *   - `default` is a neutral surface, not a primary CTA. Mirrors
 *     CF's most common button (the "Quick search" affordances,
 *     "Get Started" alert button, segmented filter buttons).
 *   - `primary` is the Theia-orange filled CTA. Reserved.
 *   - `secondary` is bordered, transparent — for less-emphasis
 *     actions next to a primary.
 *   - `ghost` has no chrome until hover. Toolbar / icon-button
 *     pattern.
 *   - `destructive` is a low-saturation red, no full pop.
 *   - `link` reads as inline text with an underline on hover.
 *
 * Sizes:
 *   - `sm` (default): 28 px tall, 12 px x-padding. CF's go-to.
 *   - `md`: 32 px / 14 px x-padding. For form-row primaries.
 *   - `lg`: 36 px / 18 px x-padding. Hero/empty-state CTAs only.
 *   - `icon`: square, no text padding. Matches sm height.
 */
const buttonVariants = cva(
  // Base: inline-flex, focusable, animates colors, disables when
  // disabled, uses our orange ring on focus-visible.
  'inline-flex items-center justify-center gap-1.5 whitespace-nowrap rounded-sm text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-3.5 [&_svg]:shrink-0',
  {
    variants: {
      variant: {
        default:
          'bg-neutral-800 text-foreground border border-neutral-700 hover:bg-neutral-700 hover:border-neutral-600',
        primary:
          'bg-primary text-primary-foreground hover:bg-theia-500 active:bg-theia-700',
        secondary:
          'border border-neutral-700 bg-transparent text-foreground hover:bg-neutral-800 hover:border-neutral-600',
        ghost:
          'bg-transparent text-muted-foreground hover:bg-neutral-800 hover:text-foreground',
        destructive:
          'bg-destructive/90 text-destructive-foreground hover:bg-destructive',
        link: 'text-primary underline-offset-4 hover:underline',
      },
      size: {
        sm: 'h-7 px-3 text-xs',
        md: 'h-8 px-3.5 text-sm',
        lg: 'h-9 px-[1.125rem] text-sm',
        icon: 'h-7 w-7',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'sm',
    },
  },
);

export interface ButtonProps
  extends ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  // `asChild` swaps the rendered <button> for whatever single
  // child the caller passes. Lets a <Link> behave like a button
  // without forfeiting button styling.
  asChild?: boolean;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : 'button';
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        {...props}
      />
    );
  },
);
Button.displayName = 'Button';

export { buttonVariants };
